import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Megaphone, Clapperboard, Workflow, ArrowRight } from "lucide-react";

const SERVICES = [
  {
    title: "Growth Strategy",
    desc: "Audits, positioning and a channel roadmap that ties every move to revenue.",
    icon: Target,
  },
  {
    title: "Paid Ads & Performance",
    desc: "Meta and Google ads managed with relentless testing and weekly optimization.",
    icon: Megaphone,
  },
  {
    title: "Creative & Content",
    desc: "Ad creative, video and copy built to stop the scroll and convert.",
    icon: Clapperboard,
  },
  {
    title: "Marketing Systems & Automation",
    desc: "CRM, email/SMS flows and dashboards that capture and compound every lead.",
    icon: Workflow,
  },
];

export const ServicesSummary = () => (
  <section data-testid="services-summary" className="mx-auto max-w-7xl px-6 py-24 md:py-32">
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-skybrand"
    >
      Services
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl md:text-5xl"
    >
      What We Do
    </motion.h2>
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
      className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600"
    >
      Full-stack, growth-focused marketing — strategy, ads, creative and systems
      under one roof, all aimed at one thing: your growth.
    </motion.p>

    <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {SERVICES.map((s, i) => (
        <motion.div
          key={s.title}
          data-testid={`service-summary-card-${i}`}
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -6 }}
          className="group flex flex-col rounded-2xl border border-slate-200/80 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_20px_50px_rgb(61,185,230,0.12)]"
        >
          <s.icon size={26} className="text-skybrand" />
          <h3 className="mt-6 font-display text-lg font-bold text-slate-900">{s.title}</h3>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.desc}</p>
          <Link
            to="/what-we-do"
            data-testid={`service-learn-more-${i}`}
            className="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-kinetic transition-colors duration-300 hover:text-kinetic-hover"
          >
            Learn more
            <ArrowRight size={12} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </motion.div>
      ))}
    </div>
  </section>
);
